//@flow strict

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import type {JOB_CUSTOM_FIELD} from 'src/types/crm';

import {useDispatch} from 'react-redux';

//$FlowFixMe[nonstrict-import]
import * as reduxApi from 'src/utils/redux-api-v2';

//$FlowFixMe[untyped-import]
import {formatMomentDateWithFormat} from 'src/utils/date-time';

import {
  CollapsibleCard,
  CollapsibleCardContent,
  CollapsibleCardAction,
} from '@spaced-out/ui-design-system/lib/components/CollapsibleCard';
import {
  BodyMedium,
  SubTitleMedium,
  SubTitleSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Shimmer} from '@spaced-out/ui-design-system/lib/components/Shimmer';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';

import {Banner} from '@spaced-out/ui-design-system/lib/components/Banner';
import ViewCustomFields from 'src/components/crm/lib/custom-fields/view-custom-fields.jsx';
import {hasCrmPermission, CRM_PERMISSIONS} from 'src/utils/crm-new/common';
import CustomFormPanel from 'src/components/crm/applicants/body/tabComponents/forms/custom-form-panel.jsx';

import css from './internal-response-form.css';


const InternalResponseForm = ({
  formInterview,
  applicationId,
  isPublicLink,
  candidateName = '',
}: {
  formInterview: {
    stepId: number,
    status: string,
    jobInternalForm: {
      validationStatus: string,
      validatorId: number,
      validationComment: string,
      updatedOn: string,
      id: number,
      customFields: {
        id: number,
        fieldType: string,
        questionTag: string,
        value: string,
        placeholderFormat?: string,
        forPdf: boolean,
        options: string[],
        customFieldId: number,
        users: {
          fullName: string,
          id: number,
        }[],
      }[],
      customForms: {
        sections: Array<{
          name: string,
          fields: Array<JOB_CUSTOM_FIELD>,
        }>,
      }[],
    },
  },
  applicationId: number,
  isPublicLink?: boolean,
  candidateName?: string,
}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();
  const [isCardOpen, setIsCardOpen] = React.useState(true);
  const [loadingStep, setLoadingStep] = React.useState(false);
  const {jobInternalForm, stepId, status} = formInterview;
  const {validationStatus, validatorId, validationComment, id} =
    jobInternalForm;
  const [stepName, setStepName] = React.useState(null);
  const [isCustomFormPanelOpen, setIsCustomFormPanelOpen] =
    React.useState(false);

  const renderAnswer = (field) => {
    if (!jobInternalForm.customFields) {
      return <BodyMedium>{`'${t('NA', 'N/A')}'`}</BodyMedium>;
    }
    const customField = jobInternalForm.customFields.find(
      (customField) => customField.id === field.customFieldId,
    );
    if (!customField) {
      return <BodyMedium>{`'${t('NA', 'N/A')}'`}</BodyMedium>;
    }

    return <ViewCustomFields field={customField} />;
  };

  React.useEffect(() => {
    const fetchStep = async () => {
      try {
        setLoadingStep(true);
        const stepData = await dispatch(
          reduxApi.get(
            `api/job-steps/${stepId}`,
            {},
            {
              apiPath: '/trmapi/',
            },
          ),
        );
        setStepName(stepData.name);
      } catch (e) {
        throw e;
      } finally {
        setLoadingStep(false);
      }
    };

    if (stepId) {
      fetchStep();
    }
  }, [stepId]);

  const approvalStatusString = `${t(
    'Submitted_by',
    'Submitted by',
  )} ${candidateName} ${t('on', 'on')} ${formatMomentDateWithFormat(
    jobInternalForm.updatedOn ?? '',
    'd MMMM, YYYY',
  )}`;

  return (
    <>
      <CollapsibleCard
        classNames={{
          wrapper: '',
        }}
        isOpen={isCardOpen}
        onChange={() => setIsCardOpen(!isCardOpen)}
        title={
          loadingStep ? (
            <Shimmer height="20px" width="200px" />
          ) : (
            <div className={css.internalResponseFormTitle}>
              <SubTitleSmall>{stepName}</SubTitleSmall>
              <Chip semantic="information">
                {t('Internal_Form', 'Internal Form')}
              </Chip>
            </div>
          )
        }
      >
        <CollapsibleCardContent className={css.cardContentWithBanner}>
          {validationStatus === 'APPROVED' ? (
            <Banner semantic="success">
              {t('Approved_by', 'Approved by')}
            </Banner>
          ) : validationStatus === 'DECLINED' ? (
            <Banner semantic="danger">{t('Declined', 'Declined')}</Banner>
          ) : (
            <Banner semantic="information">{approvalStatusString}</Banner>
          )}

          <div className={css.internalResponseCardContent}>
            {jobInternalForm.customForms &&
              jobInternalForm.customForms[0].sections &&
              jobInternalForm.customForms[0].sections.map((section) => {
                return (
                  <div className={css.formDisplaySectionContainer}>
                    <SubTitleMedium>{section.name}</SubTitleMedium>
                    <div className={css.lineBreak} />

                    {section.fields
                      .filter(
                        (field) => field.show !== 'OFF' && field.customField,
                      )
                      .map((field) => (
                        <div className={css.customField}>
                          <SubTitleSmall>{field.questionTag}</SubTitleSmall>
                          {renderAnswer(field)}
                        </div>
                      ))}
                  </div>
                );
              })}
          </div>
        </CollapsibleCardContent>
        <CollapsibleCardAction>
          <div className={css.cardActionContainer}>
            {hasCrmPermission(CRM_PERMISSIONS.EDIT_CANDIDATE_FORM) && (
              <Button
                type="tertiary"
                size="small"
                iconLeftName="pencil"
                onClick={() => setIsCustomFormPanelOpen(true)}
              >
                {t('Edit', 'Edit')}
              </Button>
            )}
          </div>
        </CollapsibleCardAction>
      </CollapsibleCard>
      {isCustomFormPanelOpen ? (
        <CustomFormPanel
          isCandidate={true}
          // $FlowFixMe[prop-missing]
          // $FlowFixMe[incompatible-type]
          formInterview={formInterview}
          isOpen={isCustomFormPanelOpen}
          onClose={() => {
            setIsCustomFormPanelOpen(false);
          }}
          formType="INTERNAL_FORM"
        />
      ) : null}
    </>
  );
};

export default InternalResponseForm;
